import { useQueryClient } from '@tanstack/react-query'
import {
  Logger,
  uniqueEntities,
  type TeamData,
  type TeamGetSuccess,
  type TeamId,
  type TeamListInput,
  type TeamMemberAddInput,
  type TeamMemberData,
  type TeamMemberRemoveInput,
  type TeamUpdateData,
  type TinyTeam,
  type UserData,
  type UserProfileData,
} from '@terros/common'
import { BLACK_BLURHASH } from '../avatar'
import { teamKey, teamListKey, tinyTeamKey } from '../keys'
import { type TeamList } from '../types'
import { partialUpdateTeamDataHelper } from './util'

const logger = new Logger('useTeamCache')

// logger.level = 'verbose'

type TeamCache = {
  clearItem: (teamId: TeamId) => void
  clearItems: (teamIds: TeamId[]) => void
  clearList: () => void
  getItem: (teamId: TeamId) => TeamData | undefined
  getTeamData: (teamId: TeamId) => TeamGetSuccess | undefined
  updateMembership: (input: TeamMemberAddInput) => void
  removeMembership: (input: TeamMemberRemoveInput) => void
  replaceUser: (user: UserProfileData) => void
  updateAvatar: (teamId: TeamId) => void
  partialUpdateTeam: (data: TeamUpdateData) => void
  setTeam: (data: TeamData) => void
  setTinyTeam: (data: TinyTeam) => void
  setTinyTeams: (data: TinyTeam[]) => void
  addTeamMember: (teamId: TeamId, user: UserData) => void
  removeChildTeam: (teamId: TeamId, childTeamId: TeamId) => void
  addChildTeam: (teamId: TeamId, childTeam: TinyTeam) => void
}

export function useTeamCache(): TeamCache {
  const queryClient = useQueryClient()

  const clearItem = (teamId: TeamId): void => {
    logger.verbose('invalidateQueries teamKey', teamId)
    queryClient.invalidateQueries({ queryKey: teamKey(teamId) })
  }

  const updateTeamMembership = (
    members: TeamMemberData[],
    { teamId, userId, locked, roleIds }: TeamMemberAddInput
  ): TeamMemberData[] => {
    if (members.some((m) => m.user.userId === userId)) {
      return members.map((m): TeamMemberData => {
        if (m.user.userId === userId) {
          return {
            ...m,
            locked,
            roleIds,
          }
        }
        return m
      })
    }
    clearItem(teamId)
    return members
  }

  const partialUpdateTeam = (data: TeamUpdateData): void => {
    const teamId = data.teamId
    queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
      if (!existing) return
      return {
        ...existing,
        team: partialUpdateTeamDataHelper(existing.team, data),
      }
    })
    queryClient.setQueriesData<TeamData[]>({ queryKey: teamListKey() }, (existing) => {
      if (!existing) return
      return existing.map((t) => (t.teamId === teamId ? partialUpdateTeamDataHelper(t, data) : t))
    })
    partialUpdateTinyTeam(data)
  }

  const partialUpdateTinyTeam = (data: TeamUpdateData): void => {
    const teamId = data.teamId
    queryClient.setQueryData<TinyTeam>(tinyTeamKey(teamId), (existing) => {
      if (!existing) return
      return partialUpdateTeamDataHelper(existing, data)
    })
  }

  const addTeamMember = (teamId: TeamId, user: UserData): void => {
    queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
      if (!existing) return
      return {
        ...existing,
        members: [...existing.members, { user, membership: 'member' }],
      }
    })
  }

  return {
    clearItem,
    clearItems: (teamIds: TeamId[]): void => {
      teamIds.forEach(clearItem)
    },
    clearList: (props: TeamListInput = {}) => {
      logger.verbose('invalidateQueries teamListKey')
      queryClient.invalidateQueries({ queryKey: teamListKey(props) })
    },
    getItem: (teamId: TeamId): TeamData | undefined => {
      logger.verbose('get teamKey', teamId)
      return queryClient.getQueryData<TeamList>(teamListKey({}))?.find((t) => teamId === t.teamId)
    },
    getTeamData: (teamId: TeamId): TeamGetSuccess | undefined => {
      return queryClient.getQueryData<TeamGetSuccess>(teamKey(teamId))
    },
    updateMembership: (input: TeamMemberAddInput): void => {
      const { teamId } = input
      queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
        if (!existing) return
        const updated = updateTeamMembership(existing.members, input)
        return {
          ...existing,
          members: updated,
        }
      })
    },
    removeMembership: (input: TeamMemberRemoveInput): void => {
      const { teamId, userId } = input
      queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
        if (!existing) return
        const updated = existing.members.filter((m) => m.user.userId !== userId)
        return {
          ...existing,
          members: updated,
        }
      })
    },
    replaceUser: (user: UserProfileData): void => {
      logger.verbose('replaceUser', user)
      queryClient.setQueriesData<TeamGetSuccess>({ queryKey: ['team'] }, (existing) => {
        if (!existing) return
        return {
          ...existing,
          members: existing.members.map((member) => {
            if (member.user.userId === user.userId) {
              return {
                ...member,
                user,
              }
            }
            return member
          }),
        }
      })
    },
    updateAvatar: (teamId: TeamId): void => {
      const update = <T extends Pick<TeamData, 'avatarUrl' | 'avatarBlurhash'>>(team: T): T => {
        return {
          ...team,
          avatarUrl: team.avatarUrl + '?v=' + Date.now(),
          avatarBlurhash: team.avatarBlurhash ?? BLACK_BLURHASH,
        }
      }
      queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
        if (!existing) return
        return {
          ...existing,
          team: update(existing.team),
          childTeams: existing.childTeams.map(update),
        }
      })
      queryClient.setQueryData<TinyTeam>(tinyTeamKey(teamId), (existing) => {
        if (!existing) return
        return update(existing)
      })
      queryClient.setQueriesData<TeamData[]>({ queryKey: teamListKey() }, (existing) => {
        if (!existing) return
        return existing.map((t) => (t.teamId === teamId ? update(t) : t))
      })
    },
    partialUpdateTeam,
    setTeam: (data: TeamData): void => partialUpdateTeam(data),
    setTinyTeam: (data: TinyTeam): void => partialUpdateTinyTeam(data),
    setTinyTeams: (data: TinyTeam[]): void => {
      for (const team of data) {
        partialUpdateTinyTeam(team)
      }
    },
    addTeamMember,
    removeChildTeam: (teamId: TeamId, childTeamId: TeamId): void => {
      queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
        if (!existing) return
        return {
          ...existing,
          childTeams: existing.childTeams.filter((t) => t.teamId !== childTeamId),
        }
      })
    },
    addChildTeam: (teamId: TeamId, childTeam: TinyTeam): void => {
      queryClient.setQueryData<TeamGetSuccess>(teamKey(teamId), (existing) => {
        if (!existing) return
        return {
          ...existing,
          childTeams: uniqueEntities([...existing.childTeams, childTeam], (t) => t.teamId),
        }
      })
    },
  }
}
